import React, { useState, useEffect } from 'react';
import { appointmentAPI } from '../../services/api';
import { formatDate, formatDateForInput, formatTime } from '../../utils/dateTimeHelpers';
import '../../styles/AdminStyles.css';

function AdminAppointments() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDate, setFilterDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [cleanupDays, setCleanupDays] = useState(30);

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const today = formatDateForInput(new Date());
      // Fetch all upcoming appointments from today and forward
      const data = await appointmentAPI.getAllAppointments(today, 10000, 0);
      setAppointments(data);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      alert('שגיאה בטעינת תורים');
    } finally {
      setLoading(false);
    }
  };

  const handleCleanup = async () => {
    if (
      !window.confirm(
        `האם אתה בטוח שברצונך למחוק תורים מעל ${cleanupDays} ימים?`
      )
    ) {
      return;
    }

    try {
      const result = await appointmentAPI.cleanupOldAppointments(cleanupDays);
      alert(`${result.deleted_count} תורים נמחקו בהצלחה`);
      setShowCleanupModal(false);
      fetchAppointments();
    } catch (error) {
      console.error('Error cleaning up appointments:', error);
      alert('שגיאה בניקוי תורים');
    }
  };

  const filteredAppointments = appointments.filter((apt) => {
    const matchesSearch =
      apt.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      apt.phone?.includes(searchTerm);

    const matchesDate = filterDate ? apt.training_date === filterDate : true;

    return matchesSearch && matchesDate;
  });

  const groupAppointmentsByDateAndHour = (appointments) => {
    const grouped = {};

    appointments.forEach((apt) => {
      if (!grouped[apt.training_date]) {
        grouped[apt.training_date] = {};
      }
      const hourKey = formatTime(apt.training_time);
      if (!grouped[apt.training_date][hourKey]) {
        grouped[apt.training_date][hourKey] = [];
      }
      grouped[apt.training_date][hourKey].push(apt);
    });

    return Object.keys(grouped)
      .sort()
      .map((date) => ({
        date,
        hours: Object.keys(grouped[date])
          .sort((a, b) => a.localeCompare(b))
          .map((hour) => ({
            hour,
            appointments: grouped[date][hour],
          })),
      }));
  };

  const groupedAppointments = groupAppointmentsByDateAndHour(filteredAppointments);

  const getAppointmentStats = () => {
    const now = new Date();
    const upcoming = appointments.filter(
      (apt) => new Date(`${apt.training_date} ${apt.training_time}`) > now
    );
    const past = appointments.filter(
      (apt) => new Date(`${apt.training_date} ${apt.training_time}`) <= now
    );

    return {
      total: appointments.length,
      upcoming: upcoming.length,
      past: past.length,
    };
  };

  const stats = getAppointmentStats();

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner"></div>
        <p>טוען תורים...</p>
      </div>
    );
  }

  return (
    <div className="admin-appointments">
      <div className="admin-header">
        <div>
          <h1>תורים</h1>
          <p>סך הכל {appointments.length} תורים</p>
        </div>
        <button
          className="admin-button-secondary"
          onClick={() => setShowCleanupModal(true)}
        >
          🗑️ נקה תורים ישנים
        </button>
      </div>

      {/* Stats */}
      <div className="admin-stats-row">
        <div className="admin-stat-item">
          <span className="admin-stat-label">סך הכל:</span>
          <span className="admin-stat-value">{stats.total}</span>
        </div>
        <div className="admin-stat-item">
          <span className="admin-stat-label">קרובים:</span>
          <span className="admin-stat-value">{stats.upcoming}</span>
        </div>
        <div className="admin-stat-item">
          <span className="admin-stat-label">עברו:</span>
          <span className="admin-stat-value">{stats.past}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-filters-bar">
        <input
          type="text"
          placeholder="🔍 חפש לפי שם או טלפון"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="admin-search-input"
        />
        <input
          type="date"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
          className="admin-search-input"
        />
        {(searchTerm || filterDate) && (
          <button
            className="admin-button-secondary"
            onClick={() => {
              setSearchTerm('');
              setFilterDate('');
            }}
          >
            נקה
          </button>
        )}
      </div>

      {/* Appointments List */}
      <div className="admin-appointments-list">
        {groupedAppointments.map(({ date, hours }) => (
          <div key={date} className="admin-date-group">
            <h2 className="admin-date-header">
              {formatDate(date, true)}
            </h2>

            {hours.map(({ hour, appointments }) => (
              <details key={`${date}-${hour}`} className="admin-date-group">
                <summary className="admin-date-header" style={{ cursor: 'pointer' }}>
                  שעה {hour} ({appointments.length} נרשמים)
                </summary>
                <div className="admin-table-container">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>שעה</th>
                        <th>שם מתאמן</th>
                        <th>טלפון</th>
                        <th>סוג אימון</th>
                        <th>סטטוס</th>
                      </tr>
                    </thead>
                    <tbody>
                      {appointments.map((apt, index) => {
                        const isPast =
                          new Date(`${apt.training_date} ${apt.training_time}`) <
                          new Date();

                        return (
                          <tr
                            key={index}
                            className={isPast ? 'admin-row-past' : ''}
                          >
                            <td>{formatTime(apt.training_time)}</td>
                            <td>{apt.full_name}</td>
                            <td>{apt.phone}</td>
                            <td>
                              <span className="admin-badge">
                                {apt.training_type}
                              </span>
                            </td>
                            <td>
                              {isPast ? (
                                <span className="admin-status-badge admin-status-past">
                                  עבר
                                </span>
                              ) : (
                                <span className="admin-status-badge admin-status-upcoming">
                                  קרוב
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </details>
            ))}
          </div>
        ))}

        {groupedAppointments.length === 0 && (
          <div className="admin-empty-state">
            <p>לא נמצאו תורים</p>
          </div>
        )}
      </div>

      {/* Cleanup Modal */}
      {showCleanupModal && (
        <div
          className="admin-modal-overlay"
          onClick={() => setShowCleanupModal(false)}
        >
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>נקה תורים ישנים</h2>
              <button
                className="admin-modal-close"
                onClick={() => setShowCleanupModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="admin-modal-content">
              <p>מחיקת תורים שעברו תעזור לשמור על המערכת מסודרת ומהירה.</p>

              <div className="admin-form-group">
                <label>מחק תורים מעל (ימים):</label>
                <input
                  type="number"
                  value={cleanupDays}
                  onChange={(e) => setCleanupDays(e.target.value)}
                  min="1"
                  max="365"
                />
              </div>

              <div className="admin-warning-box">
                ⚠️ פעולה זו תמחק לצמיתות תורים שעברו לפני {cleanupDays} ימים
              </div>
            </div>

            <div className="admin-modal-footer">
              <button
                className="admin-button-secondary"
                onClick={() => setShowCleanupModal(false)}
              >
                ביטול
              </button>
              <button className="admin-button-danger" onClick={handleCleanup}>
                מחק
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminAppointments;
